// src/components/Seguridad/Components/Catalogos/Roles/RolesCatalog.jsx
import { useEffect, useMemo, useState, useRef } from 'react';
import { ENDPOINTS } from '../../../Utils/Endpoints';
import { Models } from '../../../Utils/ModelsSeguridad';
import ConfirmDeleteDialog from '../../../../security/ConfirmDeleteDialog';
import { normalizeValue } from '../../../../../utils/Texts';
import axios from 'axios';
import Modal from './RolesModal';
import sigmaDelete from '../../../../Pages/img/Borrar.png';
import sigmaEdit from '../../../../Pages/img/Editar.png';
import AlertDialog from '../../../../Lms/Components/Shared/AlertDialog';
import { getSessionEmpresaId, isSessionSuperAdmin } from '../../../../../utils/SessionUser';
import { empresasNormalize } from '../../../../General/Objetos/EmpresasModel';
import { useResourcePermissions } from '../../../../../utils/useResourcePermissions';

const PAGE_SIZE = 10;
const nombreTabla = 'Catálogo de Roles';
const endpoint = ENDPOINTS.roles;
const endpointById = ENDPOINTS.rolById;
const modelo = Models.roles;

const maps = {
  mapCreate: (form, idEmpresa) => ({
    nombreRol: form.nombre,
    descripcion: form.descripcion,
    esSistema: !!form.esSistema,
    idEmpresa: idEmpresa,
  }),
  mapUpdate: (id, form, idEmpresa) => ({
    idRol: id,
    nombreRol: form.nombre,
    descripcion: form.descripcion,
    esSistema: !!form.esSistema,
    idEmpresa: idEmpresa,
  }),
};

export default function Catalog() {
  const { puedeCrear, puedeEditar, puedeEliminar } = useResourcePermissions('Roles');

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [query, setQuery] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('new');
  const [selectedId, setSelectedId] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState({ id: 0, name: '' });
  const [editData, setEditData] = useState(null);
  const [editingRowId, setEditingRowId] = useState(null);
  const [page, setPage] = useState(1);
  const [alertConfig, setAlertConfig] = useState({ open: false, message: '', title: 'Información', variant: 'info' });

  const [empresas, setEmpresas] = useState([]);
  const empresasIndex = useMemo(() => {
    const map = {};
    empresas.forEach((e) => {
      const eId = e.idEmpresa ?? e.IdEmpresa;
      if (eId != null) map[eId] = e;
    });
    return map;
  }, [empresas]);

  const userEmpresaId = getSessionEmpresaId();
  const isSuperAdmin = isSessionSuperAdmin();

  const triggerAlert = (message, variant = 'error', title = 'Error') => {
    setModalOpen(false);
    setDeleteOpen(false);
    setAlertConfig({ open: true, message, variant, title });
  };

  const editorRef = useRef(null);

  useEffect(() => {
    if (modalOpen && editorRef.current) {
      editorRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [modalOpen, selectedId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [resRoles, resEmpresas] = await Promise.all([
        axios.get(endpoint),
        axios.get(ENDPOINTS.empresas)
      ]);
      setRows((resRoles.data || []).map((x) => modelo(x)));
      setEmpresas((resEmpresas.data || []).map(e => empresasNormalize(e)));
    } catch (e) {
      console.error(e);
      triggerAlert('Error al cargar roles');
    } finally {
      setLoading(false);
    }
  };

  const formatEmpresa = (r) => {
    const idEmpresa = r.idEmpresa;
    if (!idEmpresa) return 'Global';
    const emp = empresasIndex[idEmpresa];
    const nombre = emp ? (emp.nombre ?? emp.Nombre ?? emp.nombreEmpresa ?? emp.NombreEmpresa ?? emp.razonSocial ?? emp.RazonSocial) : 'Not Found';
    return normalizeValue(nombre);
  };

  useEffect(() => { loadData(); }, []);

  const filtered = useMemo(() => {
    let result = [...rows];
    
    const q = query.trim().toLowerCase();
    if (q) {
      result = result.filter((r) => {
        const name = (r.nombre || '').toLowerCase();
        const desc = (r.descripcion || '').toLowerCase();
        const empresa = (formatEmpresa(r) || '').toLowerCase();
        return name.includes(q) || desc.includes(q) || empresa.includes(q);
      });
    }

    // Ordenamiento: 1. Empresa actual primero, 2. Globales al final, 3. Alfabético
    result.sort((a, b) => {
      const aIsOwn = a.idEmpresa === userEmpresaId;
      const bIsOwn = b.idEmpresa === userEmpresaId;
      if (aIsOwn && !bIsOwn) return -1;
      if (!aIsOwn && bIsOwn) return 1;

      const aIsGlobal = a.idEmpresa == null;
      const bIsGlobal = b.idEmpresa == null;
      if (!aIsGlobal && bIsGlobal) return -1;
      if (aIsGlobal && !bIsGlobal) return 1;

      return (a.nombre || '').localeCompare(b.nombre || '');
    });

    return result;
  }, [rows, query, userEmpresaId, empresasIndex]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  useEffect(() => {
    if (page > totalPages) setPage(1);
  }, [totalPages, page]);

  const paged = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, page]);

  const handleNew = () => {
    setSelectedId(null);
    setEditData(null);
    setModalMode('new');
    setModalOpen(true);
    setEditingRowId(null);
  };

  const handleEdit = (id) => {
    const r = rows.find((x) => x.idRol === id);
    if (!r) {
      triggerAlert('No se encontró la fila en memoria.');
      return;
    }
    setSelectedId(id);
    setEditData(r);
    setModalMode('edit');
    setModalOpen(true);
    setEditingRowId(id);
  };

  const askDelete = (id, name) => {
    setDeleteTarget({ id, name });
    setDeleteOpen(true);
  };

  const doDelete = async () => {
    try {
      setLoading(true);
      await axios.delete(endpointById(deleteTarget.id));
      setDeleteOpen(false);
      setEditingRowId(null);
      await loadData();
    } catch (e) {
      console.error(e);
      triggerAlert('Error al eliminar');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (payload, mode) => {
    try {
      setLoading(true);
      if (mode === 'new') {
        const res = await axios.post(endpoint, maps.mapCreate(payload, getSessionEmpresaId()));
        const newId =
          res?.data?.idRol ??
          (typeof res?.data === 'number' ? res.data : null);
        if (newId) setEditingRowId(newId);
      } else {
        await axios.put(endpoint, maps.mapUpdate(selectedId, payload, getSessionEmpresaId()));
        setEditingRowId(selectedId);
      }

      setModalOpen(false);
      await loadData();
    } catch (e) {
      console.error(e);
      triggerAlert('Error al guardar');
    } finally {
      setLoading(false);
    }
  };

  const PageBtn = ({ label, onClick, active, disabled }) => (
    <button
      type="button"
      className={`roles-page-btn ${active ? 'is-active' : ''} ${disabled ? 'is-disabled' : ''}`}
      onClick={onClick}
      disabled={disabled}
    >
      {label}
    </button>
  );

  const pageNumbers = (() => {
    const maxToShow = 5;
    let start = Math.max(1, page - 2);
    let end = Math.min(totalPages, start + maxToShow - 1);
    start = Math.max(1, end - maxToShow + 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  })();

  const startIndex = filtered.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const endIndex = Math.min(page * PAGE_SIZE, filtered.length);

  return (
    <div className="panel-sigma-main">
      <div className="roles-shell">
        <div className="roles-topbar">
          <h2 className="roles-title">{nombreTabla}</h2>

          <input
            className="roles-search"
            placeholder="Buscar por nombre o descripción…"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setPage(1);
            }}
          />
          {puedeCrear && (
            <button className="btn-action" onClick={handleNew}>
              ＋ Nuevo
            </button>
          )}
        </div>

        <div className="roles-table-wrap">
          <div className="roles-table-scroller">
            <table className="roles-table">
              <thead>
                <tr>
                  <th>Nombre del rol</th>
                  <th>Descripción</th>
                  <th>Empresa</th>
                  {puedeEditar && <th style={{ textAlign: 'center' }}>Editar</th>}
                  {puedeEliminar && <th style={{ textAlign: 'center' }}>Borrar</th>}
                </tr>
              </thead>
              <tbody>
                {paged.map((r) => (
                  <tr key={r.idRol}>
                    <td>
                      <strong>{normalizeValue(r.nombre)}</strong>
                    </td>
                    <td>{normalizeValue(r.descripcion)}</td>
                    <td>{formatEmpresa(r)}</td>
                    {puedeEditar && (
                      <td style={{ textAlign: 'center' }}>
                        {(!r.idEmpresa && !isSuperAdmin) ? (
                          <span style={{ fontSize: '1.25rem', opacity: 0.6 }} title="Rol global del sistema (Solo Lectura)">🔒</span>
                        ) : (
                          <button
                            type="button"
                            className="rk-iconbtn rk-iconbtn--edit"
                            onClick={() => handleEdit(r.idRol)}
                            aria-label="Editar"
                            title="Editar"
                          >
                            <img src={sigmaEdit} alt="" className="rk-ico" />
                          </button>
                        )}
                      </td>
                    )}
                    {puedeEliminar && (
                      <td style={{ textAlign: 'center' }}>
                        {(!r.idEmpresa && !isSuperAdmin) ? (
                          <span style={{ fontSize: '1.25rem', opacity: 0.6 }} title="Rol global del sistema (Solo Lectura)">🔒</span>
                        ) : (
                          <button
                            type="button"
                            className="rk-iconbtn rk-iconbtn--del"
                            onClick={() => {
                              setEditingRowId(null);
                              askDelete(r.idRol, r.nombre || 'Rol');
                            }}
                            aria-label="Borrar"
                            title="Borrar"
                          >
                            <img src={sigmaDelete} alt="" className="rk-ico" />
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
                {!loading && paged.length === 0 && (
                  <tr>
                    <td colSpan={3 + (puedeEditar ? 1 : 0) + (puedeEliminar ? 1 : 0)}>Sin resultados.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="roles-pagination">
          <div className="roles-pagination-left">
            {filtered.length > 0 ? (
              <span>
                Mostrando {startIndex}–{endIndex} de {filtered.length}
              </span>
            ) : (
              <span>0 resultados</span>
            )}
          </div>
          <div className="roles-pagination-right">
            <PageBtn
              label="‹"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
            />
            {pageNumbers.map((n) => (
              <PageBtn
                key={n}
                label={n}
                onClick={() => setPage(n)}
                active={n === page}
              />
            ))}
            <PageBtn
              label="›"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            />
          </div>
        </div>

        {modalOpen && (
          <div className="roles-editor-slot" ref={editorRef}>
            <Modal
              open={modalOpen}
              mode={modalMode}
              data={editData}
              inline
              onClose={() => setModalOpen(false)}
              onSubmit={handleSubmit}
            />
          </div>
        )}
      </div>

      <ConfirmDeleteDialog
        open={deleteOpen}
        roleName={deleteTarget.name}
        onClose={() => setDeleteOpen(false)}
        onConfirm={doDelete}
      />

      <AlertDialog
        open={alertConfig.open}
        title={alertConfig.title}
        message={alertConfig.message}
        variant={alertConfig.variant}
        onClose={() => setAlertConfig({ ...alertConfig, open: false })}
      />
    </div>
  );
}
